/**
 * @fileoverview The single error type the application throws and catches.
 * @module lib/errors/AppError
 */

/**
 * Machine-readable error codes.
 *
 * @description
 * Declared as a const object rather than a TS `enum` because the project
 * compiles with `erasableSyntaxOnly`: nothing that emits runtime code.
 */
export const ErrorCode = {
	/** The request never reached the server (offline, DNS, CORS). */
	NETWORK: "NETWORK",
	/** The request was cancelled or exceeded its deadline. */
	TIMEOUT: "TIMEOUT",
	/** The server answered with a 4xx/5xx status. */
	HTTP: "HTTP",
	/** The payload did not match the expected shape. */
	VALIDATION: "VALIDATION",
	/** Local persistence is unavailable, full, or holds corrupt data. */
	STORAGE: "STORAGE",
	/** Authentication is missing or expired. */
	UNAUTHORIZED: "UNAUTHORIZED",
	/** Authenticated, but not allowed. */
	FORBIDDEN: "FORBIDDEN",
	/** The resource does not exist. */
	NOT_FOUND: "NOT_FOUND",
	/** Anything we failed to classify. */
	UNKNOWN: "UNKNOWN",
} as const

/** Union of every supported error code. */
export type ErrorCode = (typeof ErrorCode)[keyof typeof ErrorCode]

/** Extra, non-sensitive context attached to an error for debugging. */
export type ErrorContext = Record<string, unknown>

/** Constructor options for {@link AppError}. */
export interface AppErrorOptions {
	/** Machine-readable code driving the UI copy. */
	readonly code?: ErrorCode
	/** HTTP status when the error originates from a response. */
	readonly status?: number
	/** Underlying error, kept for the stack trace. */
	readonly cause?: unknown
	/** Structured debugging context. Never put secrets in here. */
	readonly context?: ErrorContext
}

/**
 * Application error.
 *
 * @description
 * Everything that crosses a boundary — fetch, router loader, event handler —
 * is normalised into an AppError, so the UI only ever has one shape to render
 * and the logger only ever has one shape to report.
 *
 * @example
 * throw new AppError("Habitude introuvable", {
 *   code: ErrorCode.NOT_FOUND,
 *   status: 404,
 *   context: { habitId },
 * })
 */
export class AppError extends Error {
	/** Machine-readable code driving the UI copy. */
	readonly code: ErrorCode
	/** HTTP status when the error originates from a response. */
	readonly status: number | undefined
	/** Structured debugging context. */
	readonly context: ErrorContext

	/**
	 * @param message - Developer-facing message. User copy lives in the registry.
	 * @param options - Code, status, cause and context.
	 */
	constructor(message: string, options: AppErrorOptions = {}) {
		super(
			message,
			options.cause === undefined ? {} : { cause: options.cause }
		)

		this.name = "AppError"
		this.code = options.code ?? ErrorCode.UNKNOWN
		this.status = options.status
		this.context = options.context ?? {}

		// Keep the stack pointing at the throw site, not at this constructor.
		// captureStackTrace is a V8 extension: absent from the DOM lib types,
		// and absent from non-V8 engines, hence the guarded access.
		const { captureStackTrace } = Error as ErrorConstructor & {
			captureStackTrace?: (target: object, constructor?: unknown) => void
		}
		captureStackTrace?.(this, AppError)
	}

	/**
	 * Narrows an unknown value to an AppError.
	 *
	 * @param value - Any caught value.
	 * @returns True when the value is an AppError.
	 */
	static isAppError(value: unknown): value is AppError {
		return value instanceof AppError
	}

	/**
	 * Serialises the error for logging or transport.
	 *
	 * @returns A plain object safe to `JSON.stringify`.
	 */
	toJSON(): Record<string, unknown> {
		return {
			name: this.name,
			code: this.code,
			status: this.status,
			message: this.message,
			context: this.context,
		}
	}
}
